/**
 * How a secondary branch sources its catalog + stock.
 * - `shared` — reads the HQ catalog and stock (no own scope).
 * - `copy`   — own scope, seeded with a snapshot of HQ stock.
 * - `empty`  — own scope, starts with no products (the branch's
 *              `inventory_reset_at` is stamped at creation, so nothing
 *              created before the branch is visible to it).
 * Values mirror the Postgres `inventory_mode` enum.
 */
export const InventoryMode = {
  SHARED: 'shared',
  COPY: 'copy',
  EMPTY: 'empty',
} as const;
export type InventoryMode = (typeof InventoryMode)[keyof typeof InventoryMode];

/**
 * Single source of truth for "does this branch own its inventory
 * scope?" — HQ always does; a secondary branch does unless it is in
 * `shared` mode. Every backend scope/cutoff resolver and every FE
 * gate reads this instead of comparing `inventoryMode` strings.
 */
export const branchOwnsInventory = (branch: {
  isHeadquarters?: boolean | null;
  inventoryMode?: InventoryMode | string | null;
}): boolean =>
  !!branch.isHeadquarters ||
  (branch.inventoryMode ?? InventoryMode.SHARED) !== InventoryMode.SHARED;

export interface BranchOffice {
  id: string;
  businessId: string;
  code: string;
  name: string;
  description: string;
  email: string;
  phone: string;
  website: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
  latitude: string;
  longitude: string;
  status: number;
  isHeadquarters: boolean;
  timezone: string;
  locale: string;
  businessHours: {
    monday: { open: string; close: string; closed: boolean };
    tuesday: { open: string; close: string; closed: boolean };
    wednesday: { open: string; close: string; closed: boolean };
    thursday: { open: string; close: string; closed: boolean };
    friday: { open: string; close: string; closed: boolean };
    saturday: { open: string; close: string; closed: boolean };
    sunday: { closed: boolean };
  };
  allowsInventory: boolean;
  allowsSales: boolean;
  allowsPurchases: boolean;
  managerId: string;
  managerName: string;
  releasedAt: string | null;
  inventoryMode: InventoryMode;
}

export interface ReleaseBranchResponse {
  newBusinessId: string;
  newBusinessName: string;
  migratedUserCount: number;
  requiresPaymentSetup: boolean;
}

export interface CreateBranchOfficeInput {
  code: string;
  name: string;
  description?: string;
  email?: string;
  phone?: string;
  website?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string;
  isHeadquarters?: boolean;
  allowsInventory?: boolean;
  allowsSales?: boolean;
  allowsPurchases?: boolean;
  managerId?: string;
  inventoryMode?: InventoryMode;
  /**
   * Controls warehouse provisioning at branch creation:
   * - `create` (default): auto-create a warehouse under the new branch. Consumes the
   *   `extra_warehouse` quota — triggers a paywall / overage when over budget.
   * - `share`: link the new branch to `existingWarehouseId`; no new warehouse row
   *   is created, so no quota is consumed.
   */
  warehouseStrategy?: 'share' | 'create';
  /**
   * Required when `warehouseStrategy === 'share'`. UUID of the existing warehouse
   * the new branch should feed inventory from.
   */
  existingWarehouseId?: string;
}

export type UpdateBranchOfficeInput = Partial<
  Omit<CreateBranchOfficeInput, 'inventoryMode'>
>;

export interface CopyInventoryResponse {
  categoriesCopied: number;
  productsCopied: number;
  stockMovementsCopied: number;
}

/**
 * Branch deletion with data transfer. Groups the operator picks in the
 * deletion modal; FISCAL (invoices, NCF, payments, shifts, journal…) is
 * always transferred and cannot be unchecked.
 */
export const BranchTransferGroup = {
  FISCAL: 'fiscal',
  INVENTORY: 'inventory',
  PARTIES: 'parties',
  USERS: 'users',
  CONFIG: 'config',
} as const;
export type BranchTransferGroup =
  (typeof BranchTransferGroup)[keyof typeof BranchTransferGroup];

/** Groups the operator may leave unchecked (their rows are hidden under the target). */
export const OPTIONAL_TRANSFER_GROUPS: readonly BranchTransferGroup[] = [
  BranchTransferGroup.INVENTORY,
  BranchTransferGroup.PARTIES,
  BranchTransferGroup.USERS,
  BranchTransferGroup.CONFIG,
];

/** Open work that blocks deleting a branch until resolved. */
export const BranchDeleteBlocker = {
  OPEN_SHIFT: 'OPEN_SHIFT',
  OPEN_RESTAURANT_ACCOUNT: 'OPEN_RESTAURANT_ACCOUNT',
  DRAFT_INVOICE: 'DRAFT_INVOICE',
  PENDING_ECF: 'PENDING_ECF',
  COUNTING_IN_PROGRESS: 'COUNTING_IN_PROGRESS',
  ACTIVE_DEVICE_RESERVATION: 'ACTIVE_DEVICE_RESERVATION',
  /** A source user's email/username already exists at the target. */
  DUPLICATE_USER: 'DUPLICATE_USER',
} as const;
export type BranchDeleteBlocker =
  (typeof BranchDeleteBlocker)[keyof typeof BranchDeleteBlocker];

export interface BranchDeletionImpact {
  branch: { id: string; code: string; name: string };
  /** Row counts per transfer group. */
  groups: Partial<Record<BranchTransferGroup, number>>;
  blockers: BranchDeleteBlocker[];
  /** Users whose home branch is the one being deleted. */
  users: Array<{ id: string; name: string; email: string }>;
  /** Product SKUs that already exist in the target (will get a suffix). */
  skuClashes: string[];
}

export interface HardDeleteBranchInput {
  targetBranchOfficeId: string;
  /** Optional groups to transfer; fiscal always transfers. */
  groups: BranchTransferGroup[];
  confirmationName: string;
}

export interface HardDeleteBranchResult {
  deletedBranchOfficeId: string;
  targetBranchOfficeId: string;
  transferSummary: Partial<Record<BranchTransferGroup, number>>;
}

/** A deleted branch — names the origin of transferred records. */
export interface DeletedBranchOffice {
  id: string;
  code: string;
  name: string;
  targetBranchOfficeId: string;
  deletedAt: string;
}
